import { Box, Tooltip } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { LuX } from 'react-icons/lu'

import CustomChip from '@/components/Chip'
import HtmlKonvaElement from '@/components/HtmlKonvaElement'
import Icon from '@/components/Icon'
import { useAppSelector } from '@/hooks/redux'
import { getInitials } from '@/pages/stainingStudio/utils'
import { ENAMEL_BACKGROUNDS } from '@/utils/constants'
import { getTooltipPlacement } from '@/utils/helper'

let lastTooltipMap: Record<string, { x: number; y: number }> = {}

const TooltipContent = React.memo(
  ({ output, setTooltipOpen }: { output: any; setTooltipOpen: () => void }) => {
    return (
      <Box display="flex" alignItems="center" width="100%" gap={1}>
        <Box flex={1}>
          {output?.enameloptions && output.enameloptions.length > 0 && (
            <Box
              sx={{
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                fontSize: '12px',
              }}
            >
              {output.enameloptions}
            </Box>
          )}
        </Box>
        <Icon
          icon={LuX}
          size={14}
          onClick={setTooltipOpen}
          style={{ cursor: 'pointer' }}
        />
      </Box>
    )
  },
)

const EnamelTooltip = React.memo(
  ({
    keyName,
    data,
    output,
    open,
    toggle,
    close,
    onDelete,
  }: {
    keyName: string
    data: any
    output: any
    open: boolean
    toggle: (key: string) => void
    close: (key: string) => void
    onDelete: (key: string) => void
  }) => {
    const [placement, setPlacement] = useState<'right' | 'left'>('right')

    const canvasImage = useAppSelector((state) => state.app.canvasImage)

    const prevOutputRef = useRef<any>(null)

    useEffect(() => {
      const currentX = data.position?.x
      const currentY = data.position?.y

      const placementResult = getTooltipPlacement({
        keyName,
        currentX,
        currentY,
        open,
        canvasImageId: canvasImage?.id,
        output,
        prevOutputRef,
        lastTooltipMap,
      })

      if (placementResult) {
        setPlacement(placementResult)
      }
    }, [
      open,
      keyName,
      data.position?.x,
      data.position?.y,
      output,
      canvasImage?.id,
    ])
    return (
      <HtmlKonvaElement key={keyName} x={data.position?.x} y={data.position?.y}>
        <Box position="relative">
          <Box sx={{ transform: 'translate(-50%, -60%)' }}>
            <Tooltip
              open={open}
              title={
                output ? (
                  <TooltipContent
                    output={output}
                    setTooltipOpen={() => close(keyName)}
                  />
                ) : null
              }
              arrow
              placement={placement}
              sx={{ width: '270px' }}
              slotProps={{
                popper: {
                  disablePortal: true,
                },
                tooltip: {
                  sx: {
                    bgcolor: 'rgba(33, 33, 33, 0.80)',
                    color: 'white',
                    padding: '6px 8px',
                  },
                },
                arrow: {
                  sx: {
                    color: 'rgba(33, 33, 33, 0.80)',
                  },
                },
              }}
            >
              <CustomChip
                onClick={() => toggle(keyName)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'black',
                  padding: '4px 8px',
                  background: ENAMEL_BACKGROUNDS[keyName],
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: '20px',
                  '&:hover': {
                    background: ENAMEL_BACKGROUNDS[keyName],
                  },
                  '& .MuiChip-label': {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 8px 0 0px',
                    lineHeight: '20px',
                  },
                }}
                label={getInitials(keyName)}
                onDelete={() => onDelete(keyName)}
              />
            </Tooltip>
          </Box>
        </Box>
      </HtmlKonvaElement>
    )
  },
)

export default EnamelTooltip
